/**
 * Pure helpers for the fila (queue) placar — #426, ADR-VID-005 fila revisada
 * (adendo 2026-07-03).
 *
 * Mirrors the backend's vocabulary (`app/video/fila.py` FILA_STATUS_ATIVOS)
 * so the frontend never re-derives its own notion of "ativo" — só lê
 * `fila_status`/`fila_entered_at` que o backend já expõe em `LevantamentoOut`
 * (#423) e deriva exibição, nunca estado novo. Extraído em módulo próprio
 * (não dentro de `[lev_id]/page.tsx`) pra ficar trivialmente testável — mesmo
 * padrão de `results/helpers.ts` (Wave 10).
 */

import type { LevantamentoFilaStatus, LevantamentoOut } from "@/types/video";

/** Estados que ocupam 1 vaga de simultaneidade (espelha FILA_STATUS_ATIVOS
 * em `app/video/fila.py`). */
export const FILA_STATUS_ATIVOS: LevantamentoFilaStatus[] = [
  "processando",
  "pos_processamento",
];

/** Quantos levantamentos (dentre os fornecidos) ocupam vaga agora. */
export function countAtivos(levantamentos: LevantamentoOut[]): number {
  return levantamentos.filter((l) => FILA_STATUS_ATIVOS.includes(l.fila_status))
    .length;
}

/** Total de levantamentos 'na_fila' no momento (denominador do placar). */
export function countNaFila(levantamentos: LevantamentoOut[]): number {
  return levantamentos.filter((l) => l.fila_status === "na_fila").length;
}

/**
 * Estados em que a fila AVANÇA sozinha no backend, sem nenhuma ação do
 * usuário (dispatcher/worker mexem em background) — mesmo vocabulário do
 * placar de `upload.html` (`FILA_STATUS_EM_ANDAMENTO`). Difere de
 * `FILA_STATUS_ATIVOS` (que só conta quem OCUPA vaga): `na_fila` não ocupa
 * vaga, mas sua posição muda conforme os ativos avançam — por isso também
 * entra aqui. `erro` fica DE FORA de propósito: nada avança sozinho a
 * partir de erro (reprocesso é manual, ADR-VID-005), então pollar não
 * traria nada de novo.
 */
export const FILA_STATUS_EM_ANDAMENTO: LevantamentoFilaStatus[] = [
  "na_fila",
  "processando",
  "pos_processamento",
];

/**
 * true se houver PELO MENOS UM levantamento (o único, ou algum da lista)
 * num `fila_status` que avança sozinho — usado como gate de
 * `refetchInterval` (#426 review) pra não pollar à toa quando não há nada
 * em andamento (fila vazia/parada em aberto/erro/pronto_para_revisao).
 */
export function hasFilaEmAndamento(
  data:
    | Pick<LevantamentoOut, "fila_status">
    | Pick<LevantamentoOut, "fila_status">[]
    | undefined,
): boolean {
  if (!data) return false;
  const list = Array.isArray(data) ? data : [data];
  return list.some((l) => FILA_STATUS_EM_ANDAMENTO.includes(l.fila_status));
}

/**
 * Posição FIFO (1-based) de `levId` entre os levantamentos 'na_fila',
 * ordenados por `fila_entered_at` ascendente — mesmo ORDER BY do dispatcher
 * real (`claim_next_for_dispatch`, `app/video/fila.py`). Retorna `null` se
 * `levId` não estiver 'na_fila' ou não constar na lista fornecida — nunca
 * fabrica um número (anti-fabricação, família R-VID-003/004).
 */
export function computeFilaPosition(
  levantamentos: LevantamentoOut[],
  levId: string,
): number | null {
  const naFila = levantamentos
    .filter((l) => l.fila_status === "na_fila")
    .slice()
    .sort((a, b) => {
      const ta = a.fila_entered_at ? Date.parse(a.fila_entered_at) : Infinity;
      const tb = b.fila_entered_at ? Date.parse(b.fila_entered_at) : Infinity;
      return ta - tb;
    });
  const idx = naFila.findIndex((l) => l.id === levId);
  return idx === -1 ? null : idx + 1;
}

const ETAPA_LABEL: Partial<Record<LevantamentoFilaStatus, string>> = {
  processando: "processamento",
  pos_processamento: "pós-processamento",
};

export interface FilaPlacarInfo {
  status: LevantamentoFilaStatus;
  /** Texto principal do placar. */
  headline: string;
  /** Detalhe secundário (ex.: mensagem de erro persistida). null = sem detalhe. */
  detail: string | null;
  /** true só em 'erro' — habilita o botão Reprocessar (#426 gesto 2). */
  canReprocess: boolean;
}

/**
 * Deriva o texto do placar da fila (#426 gesto 3) a partir do `fila_status`
 * do levantamento + do snapshot de todos os levantamentos visíveis (pra
 * posição FIFO) + do teto configurado (opcional — `null` quando
 * `GET /videos/config` ainda não carregou; degrada omitindo "/teto", nunca
 * fabricando o número).
 */
export function describeFilaPlacar(
  lev: Pick<
    LevantamentoOut,
    "id" | "fila_status" | "fila_error_message" | "fila_entered_at"
  >,
  allLevantamentos: LevantamentoOut[],
  filaMaxConcurrent: number | null,
): FilaPlacarInfo {
  const status = lev.fila_status;

  if (status === "na_fila") {
    const posicao = computeFilaPosition(allLevantamentos, lev.id);
    const total = countNaFila(allLevantamentos);
    const ativos = countAtivos(allLevantamentos);
    const posicaoTxt =
      posicao !== null
        ? `posição ${posicao} de ${total}`
        : "aguardando posição";
    const tetoTxt =
      filaMaxConcurrent !== null
        ? ` — ${ativos}/${filaMaxConcurrent} processando`
        : "";
    return {
      status,
      headline: `Aguardando vaga — ${posicaoTxt}${tetoTxt}`,
      detail: null,
      canReprocess: false,
    };
  }

  if (status === "processando" || status === "pos_processamento") {
    return {
      status,
      headline: `Processando — etapa: ${ETAPA_LABEL[status]}`,
      detail: null,
      canReprocess: false,
    };
  }

  if (status === "erro") {
    return {
      status,
      headline: "Erro no processamento da fila",
      detail: lev.fila_error_message,
      canReprocess: true,
    };
  }

  if (status === "pronto_para_revisao") {
    return {
      status,
      headline: "Pronto para revisão",
      detail: null,
      canReprocess: false,
    };
  }

  // "aberto" — ainda não despachado (nenhum clique em Processar/checkbox ainda).
  return {
    status,
    headline: "Aguardando despacho",
    detail: null,
    canReprocess: false,
  };
}

// ---------------------------------------------------------------------------
// Badge compacto (lista de levantamentos) — rótulo curto + cor por estado.
// ---------------------------------------------------------------------------

const FILA_STATUS_SHORT_LABEL: Record<LevantamentoFilaStatus, string> = {
  aberto: "Aberto",
  na_fila: "Na fila",
  processando: "Processando",
  pos_processamento: "Pós-proc.",
  pronto_para_revisao: "Pronto",
  erro: "Erro",
};

/** Rótulo curto pra badge de tabela (headline completo é verboso demais pra célula). */
export function filaStatusShortLabel(status: LevantamentoFilaStatus): string {
  return FILA_STATUS_SHORT_LABEL[status];
}

const FILA_STATUS_COLOR_CLS: Record<LevantamentoFilaStatus, string> = {
  aberto: "bg-tide-text-muted/20 text-tide-text-muted",
  na_fila: "bg-amber-500/15 text-amber-600 dark:text-amber-400",
  processando: "bg-yellow-500/15 text-yellow-400",
  pos_processamento: "bg-yellow-500/15 text-yellow-400",
  pronto_para_revisao: "bg-green-500/15 text-green-400",
  erro: "bg-red-500/15 text-red-400",
};

/** Classe Tailwind do badge compacto por estado — mesma paleta do StatusPill legado. */
export function filaStatusColorCls(status: LevantamentoFilaStatus): string {
  return FILA_STATUS_COLOR_CLS[status];
}
